import { Chart } from "@antv/g2";
import { Grid } from "antd";
import * as _ from "@antv/util";
import { useEffect, useMemo, useRef, useState } from "react";
import { Input, Button, theme } from "antd";
import { SearchOutlined, FullscreenOutlined } from "@ant-design/icons";

const { useBreakpoint } = Grid;

export default function Heatmap({
  data = {},
  x = "datetime",
  y = "name",
  color = "value",
  height = 500,
  yAxisTitle = "Device Name",
  yLabelFormatter = (d) => d,
  xAxisTitle = "Date Time",
  xLabelFormatter = (d) => d,
  xValueFormatter = (d) => d,
  yValueFormatter = (d) => d,
  colorValueFormatter = (d) => d,
  onFullscreen,
}) {
  const screens = useBreakpoint();

  const container = _.uniqueId("heatmap-");
  const chartRef = useRef(null);
  const [searchString, setSearchString] = useState(null);
  const ref = useRef(null);
  const { token } = theme.useToken();

  const heatmapData = useMemo(() => {
    let result = data;
    if (searchString != null && searchString != undefined && data != null) {
      result = data?.filter(
        (item) =>
          item[y].toUpperCase().indexOf(searchString.toUpperCase()) > -1,
      );
    }
    return result;
  }, [data, searchString]);

  useEffect(() => {
    const chart = new Chart({
      container: container,
      height: height,
      autoFit: true,
      marginBottom: 30,
    });

    chart
      .cell()
      .data(heatmapData)
      .encode("x", x)
      .encode("y", y)
      .encode("color", color)
      // .transform({ type: "sortX", by: "x", reverse: true }) // if want to sort the data in reverse order can uncomment this one
      .style("inset", 0.5)
      .axis("y", {
        title: yAxisTitle,
        labelAutoRotate: false,
        labelFill: token.colorTextBase,
        labelFormatter: yLabelFormatter,
        labelAutoEllipsis: true,
      })
      .axis("x", {
        title: xAxisTitle,
        position: "top",
        labelFill: token.colorTextBase,
        labelFormatter: xLabelFormatter,
        labelAutoEllipsis: true,
      })
      .scale("color", {
        palette: "greens",
        relations: [
          [(d) => d === null, "#eee"],
          [0, "#fff"],
        ],
      })
      // .scrollbar("x", {
      //   style: {
      //     thumbFill: token.colorTextBase,
      //     thumbFillOpacity: 0.7,
      //   },
      //   ratio: 1,
      // })
      // .scrollbar("y", {
      //   style: {
      //     thumbFill: token.colorTextBase,
      //     thumbFillOpacity: 0.7,
      //   },
      //   ratio: 0.8,
      // })
      .tooltip({
        title: (d) => d[y],
        items: [
          {
            name: xAxisTitle,
            channel: "x",
            valueFormatter: xValueFormatter,
          },
          {
            name: color,
            channel: "color",
            valueFormatter: colorValueFormatter,
          },
        ],
      });

    chart.options({
      legend: {
        color: {
          labelFill: token.colorTextBase,
          titleFill: token.colorTextBase,
          slidable: false,
          handle: false,
          // labelFormatter: (d) => d.value?.toFixed(2),
        },
      },
    });

    document.getElementById(container).innerHTML = "";
    chartRef.current = chart;
    chart.render();

    const chartContainer = document.getElementById(container);
    const resizeObserver = new ResizeObserver(() => {
      chartRef.current.forceFit();
    });
    resizeObserver.observe(chartContainer.parentElement);

    return () => {
      chartRef.current.destroy();
      resizeObserver.disconnect();
    };
  }, []);

  useEffect(() => {
    let chart = chartRef.current;

    const ySize = [...new Set(heatmapData.map((item) => item[y]))].length;
    const xSize = [...new Set(heatmapData.map((item) => item[x]))].length;

    chart.clear();
    chart
      .cell()
      .data(heatmapData)
      .encode("x", x)
      .encode("y", y)
      .encode("color", color)
      // .transform({ type: "sortX", by: "x", reverse: true })
      .transform({ type: "sortY", by: "y" })
      .slider("x", null)
      .style("inset", 0.5)
      .axis("y", {
        title: yAxisTitle,
        titleFill: token.colorTextBase,
        labelAutoRotate: false,
        labelFill: token.colorTextBase,
        labelFormatter: yLabelFormatter,
        labelAutoHide: false,
        labelAutoEllipsis: true,
      })
      .axis("x", {
        title: xAxisTitle,
        titleFill: token.colorTextBase,
        position: "top",
        labelFill: token.colorTextBase,
        labelFormatter: xLabelFormatter,
        labelAutoEllipsis: true,
      })
      .scale("color", {
        palette: "greens",
        relations: [
          [(d) => d === null, "#eee"],
          [0, "#fff"],
        ],
      })
      .scrollbar(
        "y",
        ySize > 10
          ? {
              ratio: 10 / ySize,
              style: {
                thumbFill: token.colorTextBase,
                thumbFillOpacity: 0.3,
              },
            }
          : false,
      )
      .scrollbar(
        "x",
        xSize > 10
          ? {
              ratio: 10 / xSize,
              style: {
                thumbFill: token.colorTextBase,
                thumbFillOpacity: 0.3,
              },
            }
          : false,
      )
      .tooltip({
        title: (d) => d[y],
        items: [
          {
            name: "Date",
            channel: "x",
            valueFormatter: xValueFormatter,
          },
          {
            name: "Performance Ratio",
            channel: "color",
            valueFormatter: colorValueFormatter,
          },
        ],
      });

    chart.options({
      legend: {
        color: {
          labelFill: token.colorTextBase,
          titleFill: token.colorTextBase,
          slidable: false,
          handle: false,
          // labelFormatter: (d) => d.value?.toFixed(2),
        },
      },
    });

    chartRef.current = chart;
    chart.render();
  }, [token, yAxisTitle, xAxisTitle]);

  useEffect(() => {
    chartRef.current.changeData(heatmapData);
  }, [heatmapData]);

  return (
    <div ref={ref} style={{ position: "relative" }}>
      <div id={container}></div>
      {(screens.lg || screens.xxl) && (
        <div
          style={{
            position: "absolute",
            right: 5,
            top: 22,
            display: "flex",
            alignItems: "center",
            gap: 4,
          }}
        >
          <Input
            style={{ width: 180 }}
            allowClear
            placeholder="Search Device"
            onChange={(e) => setSearchString(e.target.value)}
            addonAfter={<SearchOutlined />}
          />
          {onFullscreen && (
            <Button
              type="text"
              icon={<FullscreenOutlined />}
              onClick={onFullscreen}
            />
          )}
        </div>
      )}
    </div>
  );
}
